/**
 * ApplyToExistingModal — Phase R10.4d (2026-07-05)
 *
 * Owner-triggered backfill that applies an outlet mapping's branch
 * assignment to **existing** unassigned receipt_claims
 * (approved + rejected + pending_owner_review).
 *
 * State machine:
 *   fetching  → GET dry-run preview
 *   confirm   → N > 0 && N <= hardCap → Cancel / Apply buttons
 *   empty     → N == 0 → Close only
 *   overcap   → N > 1000 → Close only
 *   executing → POST live
 *   result    → Updated N claims → Close
 *   error     → error banner + Close
 *
 * Props:
 *   mapping            — { id, original_detected_outlet, mapped_branch_id, status, ... }
 *   branches           — [{ id, name }, …] (for display)
 *   onClose()          — called on dismiss (modal container unmounts)
 *   onApplied()        — called AFTER a successful live apply (parent should
 *                        refetch the saved-mappings list)
 */
import { useEffect, useState, useMemo } from 'react';
import { X, Loader2, Zap, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';

const STATES = Object.freeze({
  FETCHING:  'fetching',
  CONFIRM:   'confirm',
  EMPTY:     'empty',
  OVERCAP:   'overcap',
  EXECUTING: 'executing',
  RESULT:    'result',
  ERROR:     'error',
});

export default function ApplyToExistingModal({ mapping, branches = [], onClose, onApplied }) {
  const token = useAuthStore((s) => s.token);
  const [phase, setPhase]       = useState(STATES.FETCHING);
  const [dryRun, setDryRun]     = useState(null);
  const [result, setResult]     = useState(null);
  const [err, setErr]           = useState(null);

  const branchName = useMemo(() => {
    const match = branches.find((b) => b.id === mapping?.mapped_branch_id);
    return match?.name ?? '—';
  }, [branches, mapping]);

  // ------------------------------------------------------------------
  // Phase 1 — fetch dry-run preview on mount.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!mapping?.id || !token) return undefined;
    let aborted = false;
    (async () => {
      try {
        const r = await api.ownerApplyOutletMappingToExisting(
          token, mapping.id, { dryRun: true },
        );
        if (aborted) return;
        setDryRun(r);
        const cap = Number(r?.hard_cap ?? 1000);
        if ((r?.candidate_count ?? 0) === 0) setPhase(STATES.EMPTY);
        else if ((r?.candidate_count ?? 0) > cap) setPhase(STATES.OVERCAP);
        else setPhase(STATES.CONFIRM);
      } catch (e) {
        if (aborted) return;
        setErr(e?.detail ?? e);
        setPhase(STATES.ERROR);
      }
    })();
    return () => { aborted = true; };
  }, [mapping?.id, token]);

  const close = () => {
    if (phase === STATES.FETCHING || phase === STATES.EXECUTING) return;
    onClose?.();
  };

  const confirmApply = async () => {
    setPhase(STATES.EXECUTING);
    try {
      const r = await api.ownerApplyOutletMappingToExisting(
        token, mapping.id, { dryRun: false },
      );
      setResult(r);
      setPhase(STATES.RESULT);
      toast.success(`Applied to ${r?.updated_count ?? 0} claims.`);
      onApplied?.(r);
    } catch (e) {
      setErr(e?.detail ?? e);
      setPhase(STATES.ERROR);
      toast.error(e?.detail?.message ?? 'Could not apply mapping');
    }
  };

  const errMessage =
    (err && typeof err === 'object' && err.message) ||
    (typeof err === 'string' ? err : null) ||
    'Something went wrong.';

  const n = dryRun?.candidate_count ?? 0;
  const byStatus = dryRun?.by_status ?? {};
  const nApproved = byStatus.approved ?? 0;
  const nPending  = byStatus.pending_owner_review ?? 0;
  const nRejected = byStatus.rejected ?? 0;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      data-testid="outlet-mapping-apply-modal"
      onClick={close}
    >
      <div
        className="w-full max-w-lg rounded-3xl border border-white/10 bg-[#15151B] p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-gold mb-1">
              Outlet Mapping
            </div>
            <h2 className="text-xl font-bold text-white">
              Apply {mapping?.original_detected_outlet || '—'} → {branchName}
            </h2>
          </div>
          <button
            type="button"
            onClick={close}
            disabled={phase === STATES.FETCHING || phase === STATES.EXECUTING}
            data-testid="outlet-mapping-apply-modal-close"
            className="p-1 rounded-lg text-white/50 hover:text-white hover:bg-white/5 disabled:opacity-40"
          >
            <X size={16} />
          </button>
        </div>

        {phase === STATES.FETCHING ? (
          <div
            className="py-10 flex items-center justify-center gap-2 text-white/55 text-sm"
            data-testid="outlet-mapping-apply-fetching"
          >
            <Loader2 size={16} className="animate-spin text-gold" />
            Checking eligible claims…
          </div>
        ) : null}

        {phase === STATES.EMPTY ? (
          <div
            className="rounded-2xl border border-dashed border-white/10 bg-white/[0.03] p-6 text-center"
            data-testid="outlet-mapping-apply-empty"
          >
            <div className="text-sm text-white/75 mb-1">
              No eligible unassigned claims found.
            </div>
            <div className="text-xs text-white/50">
              All matching claims are already assigned to a branch (or were
              manually tagged by an owner).
            </div>
          </div>
        ) : null}

        {phase === STATES.OVERCAP ? (
          <div
            className="rounded-2xl border border-amber-400/30 bg-amber-400/10 p-5"
            data-testid="outlet-mapping-apply-overcap"
          >
            <div className="flex items-start gap-2 mb-1">
              <AlertTriangle size={16} className="text-amber-300 mt-0.5" />
              <div className="text-sm font-semibold text-amber-200">
                Scope too large — hard-cap of {dryRun?.hard_cap ?? 1000} per call
              </div>
            </div>
            <div className="text-xs text-amber-100/80">
              {n} candidate claims match this mapping. Contact support for
              bulk-mode execution.
            </div>
          </div>
        ) : null}

        {phase === STATES.CONFIRM ? (
          <div data-testid="outlet-mapping-apply-confirm">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 mb-3">
              <div className="text-[11px] uppercase tracking-widest text-white/50 font-semibold mb-2">
                Preview
              </div>
              <div
                className="text-sm text-white mb-2"
                data-testid="outlet-mapping-apply-count"
              >
                This will assign the branch to{' '}
                <span className="font-bold text-gold">{n}</span>{' '}
                existing unassigned claim{n === 1 ? '' : 's'}.
              </div>
              <div
                className="flex items-center gap-3 flex-wrap text-xs text-white/70"
                data-testid="outlet-mapping-apply-breakdown"
              >
                <span>Approved: <span className="font-semibold text-white">{nApproved}</span></span>
                <span className="text-white/30">·</span>
                <span>Pending: <span className="font-semibold text-white">{nPending}</span></span>
                <span className="text-white/30">·</span>
                <span>Rejected: <span className="font-semibold text-white">{nRejected}</span></span>
              </div>
            </div>

            <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3 text-xs text-white/60 leading-5">
              <strong className="text-white/80">No</strong> approval status, receipt data,
              duplicate decisions, or stamps will be modified. Only{' '}
              <code className="text-white/80">branch_id</code> is set on each matching claim.
            </div>
          </div>
        ) : null}

        {phase === STATES.EXECUTING ? (
          <div
            className="py-10 flex items-center justify-center gap-2 text-white/55 text-sm"
            data-testid="outlet-mapping-apply-executing"
          >
            <Loader2 size={16} className="animate-spin text-gold" />
            Applying…
          </div>
        ) : null}

        {phase === STATES.RESULT ? (
          <div
            className="rounded-2xl border border-emerald-400/30 bg-emerald-400/10 p-5"
            data-testid="outlet-mapping-apply-result"
          >
            <div className="flex items-start gap-2 mb-1">
              <CheckCircle2 size={16} className="text-emerald-300 mt-0.5" />
              <div className="text-sm font-semibold text-emerald-200">
                Updated {result?.updated_count ?? 0} claim
                {(result?.updated_count ?? 0) === 1 ? '' : 's'}.
              </div>
            </div>
            {(result?.skipped_count ?? 0) > 0 ? (
              <div className="text-xs text-emerald-100/80 mt-1">
                Skipped: {result.skipped_count}
              </div>
            ) : null}
          </div>
        ) : null}

        {phase === STATES.ERROR ? (
          <div
            className="rounded-2xl border border-red-400/30 bg-red-400/10 p-5"
            data-testid="outlet-mapping-apply-error"
          >
            <div className="flex items-start gap-2 mb-1">
              <AlertTriangle size={16} className="text-red-300 mt-0.5" />
              <div className="text-sm font-semibold text-red-200">
                Something went wrong
              </div>
            </div>
            <div className="text-xs text-red-100/80">{errMessage}</div>
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-2 pt-5">
          {phase === STATES.CONFIRM ? (
            <>
              <button
                type="button"
                onClick={close}
                data-testid="outlet-mapping-apply-cancel"
                className="px-4 py-2 text-sm text-white/70 hover:text-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmApply}
                data-testid="outlet-mapping-apply-submit"
                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-black font-semibold rounded-xl px-4 py-2 text-sm"
              >
                <Zap size={14} />
                Apply to {n} Claim{n === 1 ? '' : 's'}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={close}
              disabled={phase === STATES.FETCHING || phase === STATES.EXECUTING}
              data-testid="outlet-mapping-apply-close"
              className="px-4 py-2 text-sm text-white/70 hover:text-white disabled:opacity-40"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
